/**
 * Avatar URL utilities
 * Uses Pravatar (https://pravatar.cc) for consistent placeholder photos
 * when real avatar URLs are not available. Same seed always returns same photo.
 * Pravatar provides real CC0-licensed photos from Pexels.
 */

const PRAVATAR_BASE = 'https://i.pravatar.cc'
const AVATAR_SIZE = 150

/**
 * Hash a string to a numeric ID for consistent avatar selection.
 * @param {string} str - Input string
 * @returns {number} Numeric hash
 */
function hashToId(str) {
  let hash = 0
  const s = String(str).trim()
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

/**
 * Generate a placeholder avatar URL from Pravatar.
 * Returns real photos - same seed always returns same photo.
 * @param {string} seed - Unique identifier (name, account_id, etc.) - same seed = same photo
 * @param {number} [size=150] - Avatar size in pixels
 * @returns {string} Avatar URL
 */
export function getPlaceholderAvatarUrl(seed, size = AVATAR_SIZE) {
  if (!seed || String(seed).trim() === '') {
    return null
  }
  const id = hashToId(String(seed).trim())
  return `${PRAVATAR_BASE}/${size}?u=${id}`
}

/**
 * Get avatar URL - returns real URL if provided, otherwise generates placeholder.
 * @param {string|null} realUrl - Avatar URL from API/database
 * @param {string} seed - Fallback seed (name, account_id) for placeholder generation
 * @param {number} [size=64] - Size for generated placeholder
 * @returns {string|null} Avatar URL
 */
export function getAvatarUrl(realUrl, seed, size = AVATAR_SIZE) {
  if (realUrl && typeof realUrl === 'string' && realUrl.trim() !== '') {
    return realUrl
  }
  return getPlaceholderAvatarUrl(seed, size)
}
